<script setup lang="ts">
import {
  getPasskeyCredential,
  passkeysAvailable,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@clovyr/pollen/user';
import { base64URLToArrayBuffer } from '@clovyr/pollen/util/base64';

import ClovyrLogoSmall from '@/components/icons/ClovyrLogoSmall.vue';
import LockIcon from '@/components/icons/LockIcon.vue';

import {
  debugPasskey,
  localSsoCallbackPayload,
  type LocalSsoRequest,
  parseLocalSsoRequest,
} from './local_sso';
import LocalSsoDialog from './LocalSsoDialog.vue';

const request = ref<LocalSsoRequest>();
const error = ref('');
const signing = ref(false);

try {
  request.value = parseLocalSsoRequest(new URLSearchParams(window.location.search));
} catch (err) {
  error.value = err instanceof Error ? err.message : 'invalid SSO request';
}

const targetOrigin = computed(() => request.value && new URL(request.value.audience).origin);
const supportsPasskeys = computed(() => passkeysAvailable());

function submitCallback(redirectURI: string, payload: string) {
  const form = document.createElement('form');
  form.method = 'post';
  form.action = redirectURI;

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'sso';
  input.value = payload;
  form.append(input);

  document.body.append(form);
  form.submit();
}

async function continueWithPasskey() {
  if (!request.value) {
    return;
  }
  signing.value = true;
  error.value = '';
  try {
    const publicKey: PublicKeyCredentialRequestOptionsJSON = {
      challenge: request.value.challenge,
      rpId: window.location.hostname,
      userVerification: 'required',
      allowCredentials: request.value.allowCredentials,
    };
    debugPasskey('requesting passkey', {
      origin: window.location.origin,
      rpId: publicKey.rpId,
      userVerification: publicKey.userVerification,
      allowCredentials: publicKey.allowCredentials?.map((credential) => ({
        id: credential.id,
        type: credential.type,
        transports: credential.transports,
        idByteLength: base64URLToArrayBuffer(credential.id).byteLength,
      })),
      topLevel: window.self === window.top,
      userAgent: navigator.userAgent,
    });
    const assertion = await getPasskeyCredential(publicKey);
    debugPasskey('passkey selected', {
      id: assertion.credential.id,
      rawId: assertion.credential.rawId,
      idsMatch: assertion.credential.id === assertion.credential.rawId,
      authenticatorAttachment: assertion.credential.authenticatorAttachment,
      extensionResults: Object.keys(assertion.credential.clientExtensionResults),
    });
    submitCallback(
      request.value.redirectURI,
      localSsoCallbackPayload(request.value, assertion.credential),
    );
  } catch (err) {
    debugPasskey('passkey request failed', {
      name: err instanceof Error ? err.name : 'UnknownError',
      message: err instanceof Error ? err.message : String(err),
    });
    error.value = err instanceof Error ? err.message : 'passkey assertion failed';
  } finally {
    signing.value = false;
  }
}
</script>

<template>
  <LocalSsoDialog
    title="Continue to your private app"
    description="Authenticate with your Clovyr account to securely access this application."
  >
    <div class="local-sso__host" v-if="targetOrigin">
      <!-- <i class="las la-server"></i> -->
      <!-- <span>Hosted on <strong>Digital Ocean</strong></span> -->
      <span class="local-sso__target">You are signing in to {{ targetOrigin }}</span>
    </div>

    <div class="local-sso__divider"></div>

    <button
      v-if="request && supportsPasskeys"
      class="local-sso__continue"
      type="button"
      :disabled="signing"
      :aria-busy="signing"
      @click="continueWithPasskey"
    >
      <ClovyrLogoSmall class="local-sso__continue-mark" aria-hidden="true" />
      <span>{{ signing ? 'Waiting for passkey...' : 'Continue with Clovyr' }}</span>
      <i class="las la-arrow-right local-sso__continue-arrow"></i>
    </button>
    <p v-else-if="request" class="local-sso__status local-sso__status--error" role="alert">
      <i class="las la-exclamation-triangle"></i>
      <span>This browser does not support passkeys.</span>
    </p>

    <p v-if="request && supportsPasskeys" class="local-sso__redirect-note">
      You'll be securely redirected to complete authentication.
    </p>

    <p v-if="error" class="local-sso__status local-sso__status--error" role="alert">
      <i class="las la-exclamation-triangle"></i>
      <span>{{ error }}</span>
    </p>

    <div class="local-sso__trust">
      <LockIcon />
      <div>
        <h2>Private Authentication</h2>
        <p>
          Your identity is verified securely through Clovyr. Your password is never shared with or
          stored by this application.
        </p>
      </div>
    </div>
  </LocalSsoDialog>
</template>

<style lang="scss" scoped>
.err {
  font-size: 1.5rem;
}
.local-sso__host,
.local-sso__trust,
.local-sso__status {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.local-sso__host {
  padding: 20px;
  margin-top: 32px;
  color: #83769c;
  background: #f8f7fa;
  border: 1px solid #e5e3e8;
  border-radius: 20px;
  font-size: clamp(18px, 3vw, 20px);
  letter-spacing: -0.035em;
  line-height: 1.25;
}

.local-sso__host svg,
.local-sso__trust > svg,
.local-sso__status svg {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  stroke: currentcolor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.75;
}

.local-sso__host strong {
  color: #251d3a;
  font-weight: 700;
}

.local-sso__divider {
  height: 1px;
  margin: 32px 0;
  background: #dedde3;
}

.local-sso__continue {
  display: grid;
  grid-template-columns: 24px 1fr 24px;
  gap: 12px;
  align-items: center;
  width: 100%;
  min-height: 64px;
  padding: 12px 20px;
  color: #fff;
  background: #5cbd85;
  border: 0;
  border-radius: 20px;
  box-shadow: 0 0.8rem 1.5rem rgb(92 189 133 / 20%);
  cursor: pointer;
  font: inherit;
  font-size: clamp(18px, 3vw, 20px);
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 1.25;
  transition:
    background-color 150ms ease,
    transform 150ms ease,
    box-shadow 150ms ease;
}

.local-sso__continue:focus-visible {
  outline: 3px solid #251d3a;
  outline-offset: 3px;
}

.local-sso__continue:disabled {
  cursor: wait;
  opacity: 0.7;
}

.local-sso__continue:hover:not(:disabled) {
  background: #43ae70;
  box-shadow: 0 1rem 1.75rem rgb(92 189 133 / 28%);
  transform: translateY(-1px);
}

.local-sso__continue i {
  font-size: 1.5em;
}

.local-sso__continue-mark {
  justify-self: start;
}

.local-sso__continue-arrow {
  justify-self: end;
  stroke: currentcolor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
}

.local-sso__redirect-note {
  margin: 16px 0 0;
  color: #aaa3b7;
  font-size: clamp(14px, 2.6vw, 16px);
  letter-spacing: -0.025em;
  line-height: 1.5;
  text-align: center;
}

.local-sso__status {
  padding: 16px 20px;
  margin: 24px 0 0;
  border: 1px solid;
  border-radius: 16px;
  font-size: clamp(14px, 2.6vw, 16px);
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 1.35;
}

.local-sso__status--error {
  color: #cd3347;
  background: #fff7f8;
  border-color: #f3cdd2;
  i {
    font-size: 2rem;
  }
}

.local-sso__trust {
  padding: 20px;
  margin-top: 32px;
  color: #83769c;
  background: #f8f7fa;
  border: 1px solid #e5e3e8;
  border-radius: 20px;
}

.local-sso__trust > svg {
  color: #5cbd85;
}

.local-sso__trust h2,
.local-sso__trust p {
  margin: 0;
}

.local-sso__trust h2 {
  color: #251d3a;
  font-size: clamp(18px, 2.5vw, 20px);
  font-weight: 700;
  letter-spacing: -0.035em;
  line-height: 1.3;
}

.local-sso__trust p {
  margin-top: 6px;
  font-size: clamp(14px, 2.5vw, 16px);
  letter-spacing: -0.025em;
  line-height: 1.5;
}

.local-sso__visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.local-sso__status + .local-sso__trust {
  margin-top: 8px;
}

@media (width <= 40rem) {
  .local-sso__host {
    padding: 20px;
    margin-top: 28px;
    border-radius: 20px;
  }

  .local-sso__divider {
    margin: 28px 0 24px;
  }

  .local-sso__continue {
    min-height: 56px;
    border-radius: 22px;
  }

  .local-sso__trust,
  .local-sso__status {
    gap: 14px;
    padding: 20px;
    border-radius: 20px;
  }
}
</style>
